import { Box } from "@material-ui/core";
import { SPView, PCView } from "components/common/MediaQuery";
import React, { useState, useEffect } from "react";
import { PurchasingModal } from "ui/Streaming/LiveCommerce/PurchasingModal";

import StreamingActionAreaV2Goods from "./StreamingActionAreaV2Goods";
import StreamingBottomAreaCommentForSP from "./StreamingBottomAreaCommentForSP";
import StreamingSummaryColumn from "./StreamingSummaryColumn";
import { StreamingLiveCommercePurchasedToast } from "./liveCommerce/StreamingLiveCommercePurchasedToast";
import { useLiveCommerce } from "./useLiveCommerce";

import type { ActionAreaType } from "./useActionAreaType";
import type { Timestamp } from "@google-cloud/firestore";
import type { THEOPlayer } from "@spwn/types/external/external";
import type { StreamingType } from "@spwn/types/spwn/spwn";
import type { CommentFilterType } from "modules/streaming";
import type { EventInfo } from "utility";

type Props = {
  mainVideoId: string;
  currentVideoId: string;
  eventInfo: EventInfo;
  streamingType: StreamingType;
  isOpenAnnouncement: boolean;
  player: THEOPlayer;
  eventStart: Timestamp;
  THEOPlayerElementHeight: number;
  selectedCommentFilter: CommentFilterType;
  actionAreaType: ActionAreaType;
  hasDvrStartTime: boolean;
  isChatAdmin: boolean;
  isEditingAnnouncement: boolean;
  streamingAnnouncement: string;
  isLiveCommerceEnabled: boolean;
  isLiveEventEnabled: boolean;
  isOpenLiveEvent: boolean;
  handleToggleLiveEvent: () => void;
  setWatchVideo: ({ vid, prevVid }: { vid: string; prevVid?: string }) => void;
  setIsEditingAnnouncement: (isEditing: boolean) => void;
  setStreamingAnnouncement: (announcement: string) => void;
  handleToggleAnnouncement: () => void;
  onStreamingAnnouncementLocalDeleted: () => void;
  handleClickCommentNavigation: () => void;
  handleClickGoodsNavigation: () => void;
  handleChangeCommentFilter: (filter: CommentFilterType) => void;
};

const StreamingBottomArea: React.FC<Props> = (props) => {
  const { isPurchased } = useLiveCommerce();
  //SP表示の場合はこのstateで表示エリアを切り替える
  const [bottomAreaType, setBottomAreaType] =
    useState<BottomAreaType>("summary");
  const [showPurchasingModal, setShowPurchasingModal] =
    React.useState<boolean>(false);
  const [selectedPaymentType, setSelectedPaymentType] = React.useState<
    "Card" | "CVS" | "Phone" | null
  >(null);
  const [showPurchasedToast, setShowPurchasedToast] = useState(false);

  useEffect(() => {
    if (isPurchased && selectedPaymentType !== "Phone") {
      setBottomAreaType("summary");
      setShowPurchasedToast(true);
    }
  }, [isPurchased, selectedPaymentType]);
  // isPurchasedに依存するとクリーンアップが実行されてしまうためトーストの表示とタイマーは別のuseEffectにしている
  useEffect(() => {
    if (showPurchasedToast) {
      const timer = setTimeout(() => {
        setShowPurchasedToast(false);
      }, 6000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [showPurchasedToast]);

  return (
    <Box position={"relative"}>
      <PCView>
        <StreamingSummaryColumn
          mainVideoId={props.mainVideoId}
          currentVideoId={props.currentVideoId}
          eventInfo={props.eventInfo}
          streamingType={props.streamingType}
          isOpenAnnouncement={props.isOpenAnnouncement}
          isEditingAnnouncement={props.isEditingAnnouncement}
          streamingAnnouncement={props.streamingAnnouncement}
          isLiveCommerceEnabled={props.isLiveCommerceEnabled}
          isLiveEventEnabled={props.isLiveEventEnabled}
          isOpenLiveEvent={props.isOpenLiveEvent}
          isChatAdmin={props.isChatAdmin}
          handleToggleLiveEvent={props.handleToggleLiveEvent}
          setWatchVideo={props.setWatchVideo}
          setIsEditingAnnouncement={props.setIsEditingAnnouncement}
          setStreamingAnnouncement={props.setStreamingAnnouncement}
          handleToggleAnnouncement={props.handleToggleAnnouncement}
          onStreamingAnnouncementLocalDeleted={
            props.onStreamingAnnouncementLocalDeleted
          }
          handleClickCommentNavigation={props.handleClickCommentNavigation}
          handleClickGoodsNavigation={props.handleClickGoodsNavigation}
        />
      </PCView>
      <SPView>
        {bottomAreaType === "comment" &&
          props.eventInfo.isHiddenStreamingComment === false && (
            <StreamingBottomAreaCommentForSP
              player={props.player}
              eventStart={props.eventStart}
              eventInfo={props.eventInfo}
              mainVideoId={props.mainVideoId}
              THEOPlayerElementHeight={props.THEOPlayerElementHeight}
              isChatAdmin={props.isChatAdmin}
              isOpenAnnouncement={props.isOpenAnnouncement}
              handleToggleAnnouncement={props.handleToggleAnnouncement}
              isEditingAnnouncement={props.isEditingAnnouncement}
              setIsEditingAnnouncement={props.setIsEditingAnnouncement}
              streamingAnnouncement={props.streamingAnnouncement}
              setStreamingAnnouncement={props.setStreamingAnnouncement}
              onStreamingAnnouncementLocalDeleted={
                props.onStreamingAnnouncementLocalDeleted
              }
              isOpenLiveEvent={props.isOpenLiveEvent}
              handleToggleLiveEvent={props.handleToggleLiveEvent}
              actionAreaType={props.actionAreaType}
              selectedCommentFilter={props.selectedCommentFilter}
              handleChangeCommentFilter={props.handleChangeCommentFilter}
              handleCloseActionArea={() => setBottomAreaType("summary")}
              hasDvrStartTime={props.hasDvrStartTime}
              isLiveEventEnabled={props.isLiveEventEnabled}
            />
          )}
        {bottomAreaType === "goods" && (
          <StreamingActionAreaV2Goods
            eventInfo={props.eventInfo}
            mainVideoId={props.mainVideoId}
            THEOPlayerElementHeight={props.THEOPlayerElementHeight}
            isChatAdmin={props.isChatAdmin}
            isOpenAnnouncement={props.isOpenAnnouncement}
            isEditingAnnouncement={props.isEditingAnnouncement}
            selectedCommentFilter={props.selectedCommentFilter}
            streamingAnnouncement={props.streamingAnnouncement}
            setIsEditingAnnouncement={props.setIsEditingAnnouncement}
            setStreamingAnnouncement={props.setStreamingAnnouncement}
            handleToggleAnnouncement={props.handleToggleAnnouncement}
            onStreamingAnnouncementLocalDeleted={
              props.onStreamingAnnouncementLocalDeleted
            }
            handleChangeCommentFilter={props.handleChangeCommentFilter}
            handleCloseActionArea={() => setBottomAreaType("summary")}
            isLiveEventEnabled={props.isLiveEventEnabled}
            isOpenLiveEvent={props.isOpenLiveEvent}
            handleToggleLiveEvent={props.handleToggleLiveEvent}
            setSelectedPaymentType={setSelectedPaymentType}
            setShowPurchasingModal={setShowPurchasingModal}
          />
        )}
        {bottomAreaType === "summary" && (
          <StreamingSummaryColumn
            mainVideoId={props.mainVideoId}
            currentVideoId={props.currentVideoId}
            eventInfo={props.eventInfo}
            streamingType={props.streamingType}
            isOpenAnnouncement={props.isOpenAnnouncement}
            isEditingAnnouncement={props.isEditingAnnouncement}
            streamingAnnouncement={props.streamingAnnouncement}
            isLiveCommerceEnabled={props.isLiveCommerceEnabled}
            isLiveEventEnabled={props.isLiveEventEnabled}
            isOpenLiveEvent={props.isOpenLiveEvent}
            isChatAdmin={props.isChatAdmin}
            handleToggleLiveEvent={props.handleToggleLiveEvent}
            setWatchVideo={props.setWatchVideo}
            setIsEditingAnnouncement={props.setIsEditingAnnouncement}
            setStreamingAnnouncement={props.setStreamingAnnouncement}
            handleToggleAnnouncement={props.handleToggleAnnouncement}
            onStreamingAnnouncementLocalDeleted={
              props.onStreamingAnnouncementLocalDeleted
            }
            handleClickCommentNavigation={() => setBottomAreaType("comment")}
            handleClickGoodsNavigation={() => setBottomAreaType("goods")}
          />
        )}

        {showPurchasingModal && <PurchasingModal />}

        <StreamingLiveCommercePurchasedToast
          showPurchasedToast={showPurchasedToast}
        />
      </SPView>
    </Box>
  );
};

type BottomAreaType = "comment" | "goods" | "summary";

export default StreamingBottomArea;
